import type { CircularSpinnerProps } from "@/types/ui";
import { NodeStatus } from "@shared/types";
import * as styles from "./LiveLogTree.styles";

const COLORS: Record<NodeStatus, string> = {
  [NodeStatus.LOADING]: "#6ee7b7",
  [NodeStatus.SUCCESS]: "#34d399",
  [NodeStatus.ERROR]: "#f87171",
  [NodeStatus.PENDING]: "hsl(var(--muted-foreground))",
  [NodeStatus.WARN]: "#fbbf24",
};

const CircularSpinner = ({ status = NodeStatus.LOADING, size = 18 }: CircularSpinnerProps) => {
  const r = 7;
  const cx = size / 2;
  const circumference = 2 * Math.PI * r;

  const color = COLORS[status] ?? COLORS[NodeStatus.PENDING];

  if (status === NodeStatus.SUCCESS) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={styles.spinnerSvg}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth="2" />
        <path d={`M${cx - 3.5} ${cx} l2.5 2.5 4 -4`} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (status === NodeStatus.ERROR) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={styles.spinnerSvg}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth="2" />
        <line x1={cx - 3} y1={cx - 3} x2={cx + 3} y2={cx + 3} stroke={color} strokeWidth="1.8" strokeLinecap="round" />
        <line x1={cx + 3} y1={cx - 3} x2={cx - 3} y2={cx + 3} stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  if (status === NodeStatus.PENDING) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={`${styles.spinnerSvg} ${styles.spinnerPending}`}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth="2" strokeDasharray="3 3" />
      </svg>
    );
  }

  if (status === NodeStatus.WARN) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={styles.spinnerSvg}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth="2" />
        <line x1={cx} y1={cx - 3.5} x2={cx} y2={cx + 1} stroke={color} strokeWidth="1.8" strokeLinecap="round" />
        <circle cx={cx} cy={cx + 3.5} r="0.9" fill={color} />
      </svg>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={`${styles.spinnerSvg} ${styles.spinnerLoading}`}
      style={{ animationDuration: "1s" }}
    >
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="currentColor" opacity="0.3" strokeWidth="2" />
      <circle
        cx={cx}
        cy={cx}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeDasharray={`${circumference * 0.65} ${circumference * 0.35}`}
        strokeLinecap="round"
        strokeDashoffset={circumference * 0.1}
      />
    </svg>
  );
};

export { CircularSpinner };
